'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import type { Heading } from '@/lib/blog/types'
import { BLOG_CONSTANTS } from '@/lib/blog/config'

interface TableOfContentsProps {
  headings: Heading[]
  slug: string
}

export function TableOfContents({ headings, slug }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const handleIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id)
          break
        }
      }
    }, BLOG_CONSTANTS.HEADING_DEBOUNCE_MS)
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '0px 0px -50% 0px',
    })

    for (const h of headings) {
      const el = document.getElementById(h.id)
      if (el) observer.observe(el)
    }

    return () => {
      observer.disconnect()
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [headings, slug, handleIntersect])

  if (headings.length === 0) return null

  function scrollTo(id: string) {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
      setDrawerOpen(false)
    }
  }

  const tocList = (
    <nav aria-label="Table of contents">
      <ul className="space-y-2">
        {headings.map((h) => (
          <li key={h.id} className={h.level === 3 ? 'ml-4' : ''}>
            <button
              type="button"
              onClick={() => scrollTo(h.id)}
              aria-current={activeId === h.id ? 'location' : undefined}
              className={`text-left text-sm transition-colors w-full ${
                activeId === h.id
                  ? 'text-[var(--color-brand-indigo)] font-semibold'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              {h.text}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  )

  return (
    <>
      {/* Desktop: sticky sidebar */}
      <div className="hidden lg:block sticky top-20">
        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          On This Page
        </h4>
        {tocList}
      </div>

      {/* Mobile: FAB + drawer */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="fixed bottom-8 right-8 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-brand-indigo)] text-white shadow-lg"
          aria-label="Open table of contents"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="8" y1="6" x2="21" y2="6" />
            <line x1="8" y1="12" x2="21" y2="12" />
            <line x1="8" y1="18" x2="21" y2="18" />
            <line x1="3" y1="6" x2="3.01" y2="6" />
            <line x1="3" y1="12" x2="3.01" y2="12" />
            <line x1="3" y1="18" x2="3.01" y2="18" />
          </svg>
        </button>

        {drawerOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/50"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            <div className="fixed bottom-0 left-0 right-0 z-50 max-h-[60vh] overflow-y-auto rounded-t-2xl bg-[var(--color-bg-surface)] p-6 shadow-lg">
              <div className="mb-4 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
                  On This Page
                </h4>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                  aria-label="Close table of contents"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              {tocList}
            </div>
          </>
        )}
      </div>
    </>
  )
}
